import type { UseMutationResult } from '@tanstack/react-query';
import { AddAddressPromptCard } from 'app/components/add-address-prompt-card';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { InsuredUser } from 'app/components/insured-users';
import { InvitePromptCard } from 'app/components/invite-prompt-card';
import { ListingList } from 'app/components/listing-list';
import { CATALOG_GRID_MAX_WIDTH } from 'app/components/web/dashboard-layout-constants';
import { useListingResults } from 'app/components/listing-results-context';
import { PostNeedPromptCard } from 'app/components/post-need-prompt-card';
import { PostOfferPromptCard } from 'app/components/post-offer-prompt-card';
import { Tag } from 'app/components/tag';
import type { TopLoadingIndicatorState } from 'app/components/top-loading-indicator';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useOpenOffer } from 'app/hooks/use-open-offer';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import type { CustomTabFilters } from 'app/schemas/user';

import { RemoveDistanceFilterPrompt } from 'app/screens/billboard/all-listings/components/remove-distance-filter-prompt';
import {
  type FindAllListingsDTO,
  ListingType,
  type Listing,
  TransactionMode,
  ListingSubtype,
  ListingsSortBy,
} from 'app/types/listing';
import type { FilterPreferences } from 'app/types/listings-filters';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { type LastExplorationDate, type Preference } from 'app/types/preferences';
import { FeatureFlag, isFeatureEnabled, isNotWhiteLabel } from 'app/utils/helpers';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { Dispatch, SetStateAction } from 'react';
import React from 'react';
import { Trans } from 'react-i18next';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { Platform, ScrollView, View } from 'react-native';
import { TagType, useRenderedList } from './use-rendered-list';
import { useSearchAssistantBottomSheet } from './search-assistant-bottom-sheet';
import { userInfoAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronDown } from '@tabler/icons-react-native';
const theme = createDSTheme();
const EMPTY_MAP_LISTING_ID = '00000000-0000-0000-0000-000000000000';
const REMOVE_INLINE_PROMPTS = true;

interface RenderedListProps {
  appliedFilters: FilterPreferences;
  search: string;
  setSearch: (search: string) => void;
  navigation: SpecificNavigationProp<'all-listings'>;
  forcedFilters: CustomTabFilters;
  listingIds?: Listing['id'][];
  listingUserId?: Listing['userId'];
  listFormat?: 'list' | 'grid';
  setListFormat?: Dispatch<SetStateAction<'list' | 'grid'>>;
  onListingHoverChange?: (listing?: Listing) => void;
  highlightedListingId?: Listing['id'];
  hideFilterButton?: boolean;
  i18nKey?: string | null;
  isLoadingState?: TopLoadingIndicatorState;
  loadingIndicatorState?: TopLoadingIndicatorState;
  isLoggedIn?: boolean;
  lastExplorationDateMutation?: UseMutationResult<
    Preference<'lastExplorationDate'> | null,
    Error,
    LastExplorationDate | null
  >;
  newAddressBadgeState?: [boolean, Dispatch<SetStateAction<boolean>>];
  onScroll?: ((event: NativeSyntheticEvent<NativeScrollEvent>, numListings: number) => void) | undefined;
  childBelowFilters?: React.ReactNode;
  additionalFooter?: React.ReactNode;
  showRequestBanner?: boolean;
  allowSort?: boolean;
  showHighestCard?: boolean;
  withPadding?: boolean;
  showVerticalScrollIndicator?: boolean;
  filterOverrides?: Pick<FindAllListingsDTO, 'searchCenterLatitude' | 'searchCenterLongitude' | 'searchDistance'>;
  variant?: {
    viewer: 'authenticated' | 'anonymous';
    filterButton: 'visible' | 'hidden';
    requestBanner: 'visible' | 'hidden';
    sorting: 'enabled' | 'disabled';
    requestCard: 'standard' | 'highest';
    padding: 'none' | 'page';
    verticalScrollIndicator: 'visible' | 'hidden';
  };
}

function RenderedList({
  appliedFilters,
  newAddressBadgeState,
  isLoadingState: explicitLoadingState,
  loadingIndicatorState,
  isLoggedIn: explicitIsLoggedIn,
  lastExplorationDateMutation,
  forcedFilters,
  hideFilterButton: explicitHideFilterButton,
  i18nKey,
  navigation,
  onScroll,
  search,
  setSearch,
  childBelowFilters: _childBelowFilters,
  additionalFooter,
  showRequestBanner: explicitShowRequestBanner,
  allowSort: explicitAllowSort,
  showHighestCard: explicitShowHighestCard,
  withPadding: explicitWithPadding,
  showVerticalScrollIndicator: explicitShowVerticalScrollIndicator,
  setListFormat,
  listFormat,
  onListingHoverChange,
  highlightedListingId,
  listingIds,
  listingUserId,
  filterOverrides,
  variant,
}: RenderedListProps) {
  const {
    isLoggedIn,
    isLoadingState,
    hideFilterButton,
    showVerticalScrollIndicator,
    withPadding,
    showHighestCard,
    allowSort,
    showRequestBanner,
  } = getRenderedListOptions(
    explicitLoadingState,
    loadingIndicatorState,
    explicitIsLoggedIn,
    variant,
    explicitHideFilterButton,
    explicitShowRequestBanner,
    explicitAllowSort,
    explicitShowHighestCard,
    explicitWithPadding,
    explicitShowVerticalScrollIndicator,
  );
  const rl = useRenderedList({
    appliedFilters,
    search,
    setSearch,
    forcedFilters,
    isLoggedIn,
    isLoadingState,
    lastExplorationDateMutation,
    newAddressBadgeState,
    hideFilterButton,
  });

  const {
    t,
    canPublish,
    listingType,
    freeSubAvailable,
    listsServicesOnly,
    sortState,
    w,
    scrollBeginHandler,
    filters,
    showAddAddressPrompt,
    isSmallUI,
    listRef,
    promptToLookFurther,
    hideListingsSubtype,
  } = rl;
  const effectiveDisplayMode = listFormat ?? (isSmallUI ? 'grid' : 'list');

  const userInfo = useAtomValue(userInfoAtom);
  const gridColumnGap = theme.spacing['spacing.general.x4'];

  return (
    <View style={[{ flex: 1 }, Platform.OS !== 'web' && effectiveDisplayMode !== 'grid' && { width: w }]}>
      <ListingList
        numColumns={isSmallUI ? undefined : 4}
        maxGridWidth={Platform.OS === 'web' ? CATALOG_GRID_MAX_WIDTH : undefined}
        itemHorizontalSpacing={gridColumnGap}
        columnWrapperStyle={{ columnGap: gridColumnGap }}
        alwaysShowFooter={Boolean(additionalFooter)}
        showVerticalScrollIndicator={showVerticalScrollIndicator}
        withPadding={withPadding}
        contentContainerStyle={{ paddingBottom: additionalFooter ? 0 : 120 }}
        ListFooterComponentStyle={additionalFooter ? { paddingBottom: 0 } : undefined}
        showHighestCard={showHighestCard}
        ListEmptyComponent={
          <RenderedListEmpty state={rl} isLoggedIn={isLoggedIn} search={search} navigation={navigation} />
        }
        ListFooterComponent={
          <RenderedListFooter additionalFooter={additionalFooter} isLoggedIn={isLoggedIn} search={search} state={rl} />
        }
        ListHeaderComponent={
          (rl.showFiltersButton && rl.showFiltersTags) ||
          (!search && listingType === ListingType.request && rl.showInsuredInfo) ? (
            <RenderedListHeader state={rl} search={search} />
          ) : null
        }
        allowSort={allowSort}
        showRequestsBanner={showRequestBanner}
        fetchListingsEnabled={appliedFilters.isLoaded}
        filters={getCatalogListingFilters(filters, filterOverrides, listingUserId, listingIds, listingType)}
        hideListingsSubtype={hideListingsSubtype}
        i18nKey={i18nKey}
        inlinePrompts={getRenderedListInlinePrompts({
          freeSubAvailable,
          listingType,
          search,
          showAddAddressPrompt,
          t,
        })}
        innerRef={listRef}
        isLoadingState={isLoadingState}
        queryContent={
          <ListingSearchAssistant
            canPublish={canPublish}
            isLoggedIn={isLoggedIn}
            listingType={listingType}
            navigation={navigation}
            promptToLookFurther={!!promptToLookFurther}
            search={search}
            userFeatureFlags={userInfo?.featureFlags}
          />
        }
        onScroll={(e, numListings) => {
          onScroll?.(e, numListings);
        }}
        onScrollBeginDrag={() => void scrollBeginHandler()}
        sortByStartDate={listsServicesOnly}
        sortState={sortState}
        throwOnError={false} // Billboard would sometimes throw a CancelledError when the app just started - trying to see if this fixes it; ref: https://lepartageclub.atlassian.net/browse/LPC-2667
        trackSearchPerformedEvent
        metricsLabel={`all_listings_${listingType}`}
        listFormat={effectiveDisplayMode}
        setListFormat={setListFormat}
        onListingHoverChange={onListingHoverChange}
        highlightedListingId={highlightedListingId}
      />
    </View>
  );
}

export { RenderedList };

function getRenderedListInlinePrompts({
  freeSubAvailable,
  listingType,
  search,
  showAddAddressPrompt,
  t,
}: Pick<ReturnType<typeof useRenderedList>, 'freeSubAvailable' | 'listingType' | 'showAddAddressPrompt' | 't'> & {
  search: string;
}) {
  // Might be moved into the landing-page screen later, if judged necessary.
  if (listingType !== ListingType.offer || search || !isNotWhiteLabel() || REMOVE_INLINE_PROMPTS) return undefined;

  let firstPrompt: React.ReactElement;
  if (freeSubAvailable) {
    firstPrompt = (
      <PostOfferPromptCard
        buttonText="postOfferPrompt.first.button"
        newListingSource="inline_banner_5"
        title="postOfferPrompt.first.title"
      />
    );
  } else if (showAddAddressPrompt) {
    firstPrompt = (
      <AddAddressPromptCard
        newAddressSource="inline_banner_5"
        style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
      />
    );
  } else {
    firstPrompt = (
      <InvitePromptCard
        buttonText={t(`postOfferPrompt.firstAfter.button`)}
        text={t('postOfferPrompt.firstAfter.text')}
        title={t(`postOfferPrompt.firstAfter.title`)}
      />
    );
  }

  return [
    { position: 5, element: firstPrompt },
    {
      position: 20,
      element: (
        <PostOfferPromptCard
          buttonText="postOfferPrompt.second.button"
          newListingSource="inline_banner_20"
          quick
          text={t('postOfferPrompt.second.text')}
          title="postOfferPrompt.second.title"
        />
      ),
    },
  ];
}

function getCatalogListingFilters(
  filters: {
    searchCenterLatitude?: number | undefined;
    searchCenterLongitude?: number | undefined;
    searchDistance?: number | undefined;
    sortBy: ListingsSortBy;
    isLoggedIn: boolean;
    transactionModes?: TransactionMode[] | undefined;
    category: string | undefined;
    serviceCategory: string | undefined;
    tags: string[] | undefined;
    search: string;
    type: ListingType;
    subtypes: NonNullable<ListingSubtype | undefined>[];
    hideFoundRequests: boolean | undefined;
    dismissedOnly: boolean | undefined;
    hideOffersWithFee: boolean | undefined;
    groupIdsFilterActive?: true | undefined;
    addressIds: string[] | undefined;
    groupIds: string[] | undefined;
  },
  filterOverrides:
    Pick<FindAllListingsDTO, 'searchDistance' | 'searchCenterLatitude' | 'searchCenterLongitude'> | undefined,
  listingUserId: string | undefined,
  listingIds: string[] | undefined,
  listingType: ListingType,
): (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined {
  return {
    ...filters,
    ...filterOverrides,
    ...(listingUserId ? { userId: listingUserId } : {}),
    ...(listingIds ? { ids: listingIds.length > 0 ? listingIds : [EMPTY_MAP_LISTING_ID], doGrouping: false } : {}),
    // Besoins cards show views/favourites; offres babillard cards do not — skip extra DB on offers.
    includeCounts: listingType === ListingType.request,
    includeTrustPoints: true,
  };
}

function getRenderedListOptions(
  explicitLoadingState: TopLoadingIndicatorState | undefined,
  loadingIndicatorState: TopLoadingIndicatorState | undefined,
  explicitIsLoggedIn: boolean | undefined,
  variant:
    | {
        viewer: 'authenticated' | 'anonymous';
        filterButton: 'visible' | 'hidden';
        requestBanner: 'visible' | 'hidden';
        sorting: 'enabled' | 'disabled';
        requestCard: 'standard' | 'highest';
        padding: 'none' | 'page';
        verticalScrollIndicator: 'visible' | 'hidden';
      }
    | undefined,
  explicitHideFilterButton: boolean | undefined,
  explicitShowRequestBanner: boolean | undefined,
  explicitAllowSort: boolean | undefined,
  explicitShowHighestCard: boolean | undefined,
  explicitWithPadding: boolean | undefined,
  explicitShowVerticalScrollIndicator: boolean | undefined,
) {
  const isLoadingState = explicitLoadingState ?? loadingIndicatorState;
  const isLoggedIn = explicitIsLoggedIn ?? variant?.viewer === 'authenticated';
  const hideFilterButton = explicitHideFilterButton ?? variant?.filterButton === 'hidden';
  const showRequestBanner = explicitShowRequestBanner ?? variant?.requestBanner !== 'hidden';
  const allowSort = explicitAllowSort ?? variant?.sorting !== 'disabled';
  const showHighestCard = explicitShowHighestCard ?? variant?.requestCard === 'highest';
  const withPadding = explicitWithPadding ?? variant?.padding === 'page';
  const showVerticalScrollIndicator =
    explicitShowVerticalScrollIndicator ?? variant?.verticalScrollIndicator === 'visible';
  return {
    isLoggedIn,
    isLoadingState,
    hideFilterButton,
    showVerticalScrollIndicator,
    withPadding,
    showHighestCard,
    allowSort,
    showRequestBanner,
  };
}

function RenderedListHeader({ state, search }: { state: ReturnType<typeof useRenderedList>; search: string }) {
  const { listingType, showInsuredInfo, showFiltersButton, showFiltersTags } = state;
  const hasFilterTags = showFiltersButton && showFiltersTags;
  const hasInsuredInfo = !search && listingType === ListingType.request && showInsuredInfo;
  return (
    <Column gap="spacing.general.x4" marginBottom="spacing.general.x2">
      {hasFilterTags ? <RenderedListFilterTags state={state} /> : null}
      {hasInsuredInfo ? (
        <View
          style={{
            alignSelf: 'center',
            width: '100%',
            maxWidth: Platform.OS === 'web' ? CATALOG_GRID_MAX_WIDTH : undefined,
          }}
        >
          <InsuredUser otherUser={undefined} placement="explore_needs" />
        </View>
      ) : null}
    </Column>
  );
}

function RenderedListFilterTags({ state }: { state: ReturnType<typeof useRenderedList> }) {
  const { showFiltersButton, showFiltersTags } = state;
  if (!showFiltersTags || !showFiltersButton) return null;

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <Row alignItems="center" flex={1} gap="spacing.general.x2" marginTop="spacing.general.x2">
        <RenderedListTaxonomyFilterTags state={state} />
        <RenderedListOptionFilterTags state={state} />
      </Row>
    </ScrollView>
  );
}

function RenderedListTaxonomyFilterTags({ state }: { state: ReturnType<typeof useRenderedList> }) {
  const {
    buildTagText,
    categories,
    closeTag,
    searchDistance,
    selectedSubtypes,
    selectedTags,
    serviceCategories,
    showCategoriesFilterTags,
    showDistanceTag,
    showServiceCategoriesFilterTags,
    showSubtypesFilterTags,
    showTagsFilterTags,
    sortedSelectedCategories,
    sortedSelectedServiceCategories,
  } = state;
  return (
    <>
      {showDistanceTag ? (
        <Tag
          key="distance"
          onClose={() => closeTag('', TagType.Distance)}
          text={buildTagText(((searchDistance ?? 0) / 1000).toString(), [], TagType.Distance)}
        />
      ) : null}
      {showCategoriesFilterTags
        ? sortedSelectedCategories?.map((category) => (
            <Tag
              key={category.id}
              onClose={() => closeTag(category.id, TagType.Category)}
              text={buildTagText(category.id, categories, TagType.Category)}
            />
          ))
        : null}
      {showServiceCategoriesFilterTags
        ? sortedSelectedServiceCategories?.map((category) => (
            <Tag
              key={category.id}
              onClose={() => closeTag(category.id, TagType.ServiceCategory)}
              text={buildTagText(category.id, serviceCategories, TagType.ServiceCategory)}
            />
          ))
        : null}
      {showTagsFilterTags
        ? selectedTags?.map((tagId) => (
            <Tag
              key={tagId}
              onClose={() => closeTag(tagId, TagType.ListingTag)}
              text={buildTagText(tagId, [], TagType.ListingTag)}
            />
          ))
        : null}
      {showSubtypesFilterTags
        ? selectedSubtypes?.map((subtype) => (
            <Tag
              key={subtype}
              onClose={() => closeTag(subtype, TagType.Subtype)}
              text={buildTagText(subtype, serviceCategories, TagType.Subtype)}
            />
          ))
        : null}
    </>
  );
}

function RenderedListOptionFilterTags({ state }: { state: ReturnType<typeof useRenderedList> }) {
  const {
    addresses,
    buildTagText,
    closeTag,
    groups,
    selectedAddresses,
    selectedGroups,
    selectedTransactionModes,
    showAddressesFilterTags,
    showDismissedOnlyTag,
    showGroupsFilterTags,
    showHideFoundRequestsTag,
    showHideOffersWithFeeTag,
    showTransactionModeTags,
  } = state;
  return (
    <>
      {showHideFoundRequestsTag ? (
        <Tag
          key="hideFoundRequests"
          onClose={() => closeTag('', TagType.HideFoundRequests)}
          text={buildTagText('', [], TagType.HideFoundRequests)}
        />
      ) : null}
      {showDismissedOnlyTag ? (
        <Tag
          key="dismissedOnly"
          onClose={() => closeTag('', TagType.DismissedOnly)}
          text={buildTagText('', [], TagType.DismissedOnly)}
        />
      ) : null}
      {showHideOffersWithFeeTag ? (
        <Tag
          key="hideOffersWithFee"
          onClose={() => closeTag('', TagType.HideOffersWithFee)}
          text={buildTagText('', [], TagType.HideOffersWithFee)}
        />
      ) : null}
      {showTransactionModeTags
        ? (selectedTransactionModes ?? []).map((mode) => (
            <Tag
              key={`txMode-${mode}`}
              onClose={() => closeTag(mode, TagType.TransactionMode)}
              text={buildTagText(mode, [], TagType.TransactionMode)}
            />
          ))
        : null}
      {showAddressesFilterTags
        ? selectedAddresses.map((addressId) => (
            <Tag
              key={addressId}
              onClose={() => closeTag(addressId, TagType.Address)}
              text={buildTagText(addressId, addresses, TagType.Address)}
            />
          ))
        : null}
      {showGroupsFilterTags
        ? selectedGroups.map((groupId) => (
            <Tag
              key={groupId}
              onClose={() => closeTag(groupId, TagType.Group)}
              text={buildTagText(groupId, groups, TagType.Group)}
            />
          ))
        : null}
    </>
  );
}

function RenderedListEmpty({
  state,
  isLoggedIn,
  search,
  navigation,
}: {
  state: ReturnType<typeof useRenderedList>;
  isLoggedIn: boolean;
  search: string;
  navigation: RenderedListProps['navigation'];
}) {
  const { promptToLookFurther, listingType, searchDistance, setSearchDistance, listRef } = state;
  if (!isLoggedIn) {
    return <LoggedOutRenderedListEmpty navigation={navigation} state={state} />;
  }

  if (promptToLookFurther) {
    return (
      <RemoveDistanceFilterPrompt
        onTrigger={() => {
          setSearchDistance?.(MAX_RADIUS_IN_METERS);
          listRef.current?.scrollToOffset({ animated: true, offset: 0 });
        }}
        searchDistance={searchDistance ?? MAX_RADIUS_IN_METERS}
      />
    );
  }

  if (listingType === ListingType.offer && search) {
    return <SearchRenderedListEmpty search={search} state={state} />;
  }

  return <DefaultRenderedListEmpty navigation={navigation} state={state} />;
}

function LoggedOutRenderedListEmpty({
  navigation,
  state,
}: Pick<Parameters<typeof RenderedListEmpty>[0], 'navigation' | 'state'>) {
  const { t } = state;
  return (
    <EmptyGraphic
      button={{
        authorize: true,
        children: t('needLogin.primaryButtonText'),
        onPress: () => navigation.navigate('onboarding', undefined, { pop: true }),
      }}
      primary={t('needLogin.firstLine')}
      secondary={t('needLogin.secondLine')}
      source={images.target}
    />
  );
}

function SearchRenderedListEmpty({ search, state }: Pick<Parameters<typeof RenderedListEmpty>[0], 'search' | 'state'>) {
  const { canPublish, showAddAddressPrompt } = state;
  return (
    <Column alignItems="center" gap="spacing.general.x4" marginTop="spacing.general.x4">
      <Text fontSize={24} textAlign="center">
        <Trans
          components={{ strong: <Text fontWeight="700" /> }}
          i18nKey="noResultsFor"
          ns="common"
          values={{ query: search }}
        />
      </Text>

      <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />

      {canPublish ? (
        <PostNeedPromptCard
          buttonText="postNeedPrompt.noResults.button"
          itemName={search}
          newListingSource="babillard_empty"
          style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
          text="postNeedPrompt.noResults.text"
          title="postNeedPrompt.noResults.title"
          v2
        />
      ) : null}

      {showAddAddressPrompt ? <AddAddressPromptCard newAddressSource="babillard_empty" /> : null}
    </Column>
  );
}

function DefaultRenderedListEmpty({
  navigation,
  state,
}: Pick<Parameters<typeof RenderedListEmpty>[0], 'navigation' | 'state'>) {
  const { canPublish, listingType, listsServicesOnly, t } = state;
  const openNewWithGuard = useOpenNewWithGuard();
  const openOffer = useOpenOffer();
  const tWhat = getDefaultEmptyGraphicType({ canPublish, listingType, listsServicesOnly });
  const onPressEmpty = () => {
    if (tWhat === 'request') {
      openNewWithGuard(navigation, {
        newListingSource: 'babillard_empty',
        defaultValues: { type: ListingType.request },
      });
    }
    if (tWhat === 'offer') {
      openOffer(navigation, {
        newListingSource: 'babillard_empty',
        defaultValues: { type: ListingType.offer },
      });
    }
    if (tWhat === 'invite') {
      navigation.navigate('invite', undefined, { pop: true });
    }
  };

  return (
    <EmptyGraphic
      button={
        !tWhat.startsWith('nothing')
          ? {
              authorize: true,
              children: t(`empty.${tWhat}.primaryButtonText`),
              onPress: onPressEmpty,
            }
          : undefined
      }
      primary={t(`empty.${tWhat}.firstLine`)}
      secondary={t(`empty.${tWhat}.secondLine`)}
      source={images.lightBulb}
    />
  );
}

function getDefaultEmptyGraphicType({
  canPublish,
  listingType,
  listsServicesOnly,
}: Pick<ReturnType<typeof useRenderedList>, 'canPublish' | 'listingType' | 'listsServicesOnly'>) {
  if (listingType === ListingType.request && canPublish) return 'request';
  if (isFeatureEnabled(FeatureFlag.invite)) return 'invite';
  if (canPublish) return 'offer';
  return listsServicesOnly ? 'nothingServicesOnly' : 'nothing';
}

function ListingSearchAssistant(
  props: Omit<Parameters<typeof useSearchAssistantBottomSheet>[0], keyof ReturnType<typeof useListingResults>>,
) {
  const listingsState = useListingResults();
  const { bottomSheet } = useSearchAssistantBottomSheet({ ...props, ...listingsState });
  return bottomSheet;
}

function RenderedListFooter({
  additionalFooter,
  isLoggedIn,
  search,
  state,
}: {
  additionalFooter: React.ReactNode;
  isLoggedIn: boolean;
  search: string;
  state: ReturnType<typeof useRenderedList>;
}) {
  const { itemsCount } = useListingResults();
  const {
    canPublish,
    promptToLookFurther,
    listingType,
    searchDistance,
    setSearchDistance,
    listRef,
    showAddAddressPrompt,
  } = state;
  let contextualFooter: React.ReactNode;
  if (itemsCount > 0 && promptToLookFurther) {
    contextualFooter = (
      <RemoveDistanceFilterPrompt
        onTrigger={() => {
          setSearchDistance?.(MAX_RADIUS_IN_METERS);
          listRef.current?.scrollToOffset({ animated: false, offset: 0 });
        }}
        searchDistance={searchDistance ?? MAX_RADIUS_IN_METERS}
      />
    );
  } else if (itemsCount > 0 && listingType === ListingType.offer && search && isLoggedIn) {
    contextualFooter = (
      <Column alignItems="center" marginTop="spacing.general.x6">
        <Text fontSize={24} marginBottom="spacing.general.x4" textAlign="center">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey="afterResultsFor"
            ns="common"
            values={{ query: search }}
          />
        </Text>
        <View style={{ marginBottom: theme.spacing['spacing.general.x4'] }}>
          <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
        </View>
        {canPublish ? (
          <PostNeedPromptCard
            buttonText="postNeedPrompt.afterResults.button"
            itemName={search}
            newListingSource="babillard_after_search_results"
            style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
            text="postNeedPrompt.afterResults.text"
            title="postNeedPrompt.afterResults.title"
            v2
          />
        ) : null}
        {showAddAddressPrompt ? (
          <AddAddressPromptCard
            newAddressSource="babillard_after_search_results"
            style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
          />
        ) : null}
      </Column>
    );
  }
  if (!contextualFooter && !additionalFooter) return null;
  return (
    <Column>
      {contextualFooter}
      {additionalFooter}
    </Column>
  );
}
